'use client'

import { createContext, useContext, useEffect, useRef, useMemo, useState, ReactNode, useCallback } from "react";
import { toast } from "sonner";
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEditorAutosave } from "@/hooks/useEditorAutosave";
import { mapPublishedSectionsToRows, type EditorRow } from "@/lib/funnelRows"

export type PageType = "attraction" | "landing" | "checkout" | "event" | "thank-you" | "survey";

export interface Block {
  id: string;
  type: "hero" | "benefits" | "testimonials" | "form" | "cta" | "pricing" | "faq" | "image" | "video" | "navbar" | "stats" | "features" | "timeline" | "countdown" | "button" | "text" | "checkout" | 'event' | "checkout-product" | "checkout-form";
  data: any;
}

export type SectionBgMode = 'auto' | 'light' | 'dark' | 'color' | 'gradient' | 'image';

export interface SectionProps {
  backgroundColor: SectionBgMode;          // p.ej. 'auto'
  backgroundColorValue?: string;           // cuando backgroundColor === 'color'
  backgroundGradient?: string;             // cuando backgroundColor === 'gradient'
  backgroundImage?: string;                // cuando backgroundColor === 'image'
}

export interface Column {
  id: string;
  span: number;        // ancho, como en Home (12 = full)
  modules: Block[];    // los módulos que van dentro de la columna
}

export interface Section {
  id: string;
  name: string;
  columns?: Column[];
  props: SectionProps;    
  sortOrder?: Number;
}

export type FunnelActionType =
  | "community"
  | "checkout"
  | "whatsapp"
  | "thankyou"
  | "link"
  | "event";

export interface FunnelAction {
  type: FunnelActionType;
  url?: string;
  whatsapp?: {
    message?: string;
    templateId?: string;
    mode?: "redirect" | "background";
  };
  eventPageId?: number | null;
}

export interface PageSettings {
  funnelAction?: FunnelAction;
  funnel_name?: string
  theme?: "light" | "dark";
  themeId?: 'default' | 'branders_glass_dark'
  primaryColor?: string;
  secondaryColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  backgroundColor?: string;
  textColor?: string;
  fontFamily?: string;
  funnelStyle?: string
  headingFont?: string;
  backgroundGradient?: string;
  headingGradient?: string;
  customCSS?: string;
  emailSenderMode: "company" | "firstname" | "custom",
  emailSenderCustomName?: string, 
  emailSubject?: string;
  emailMessage?: string;
  emailSenderName?: string;
  emailSenderEmail?: string;
  routePath?: string; 
  facebookPixelId?: string
  googleAnalyticsId?: string
  metaTitle?: string
  metaDescription?: string
  metaImage?: string
  checkoutData?: {
    productName?: string;
    productImage?: string;
    productDescription?: string;
    amount?: number;
    features?: string[];
    bumpOffer?: {
      name?: string;
      price?: number;
      description?: string;
      image?: string;
    };
  };
}

export type FunnelPageType = 'attraction' | 'landing' | 'checkout' | 'thank-you' | string

export type FunnelNextType = 'none' | 'page' | 'exit' | string

export interface FunnelPage {
  id: string;
  type: PageType;
  name: string;
  sections: Section[];
  settings?: Partial<PageSettings>
  settingsDraft?: Partial<PageSettings>
  next_type: FunnelNextType
  next_page_id: string | null
  next: {
    type: FunnelNextType
    pageId: string | null
  }
}

export interface Funnel {
  id: string;
  name: string;
  description?: string;
  pages: FunnelPage[];
  settings?: PageSettings;
  createdAt?: Date;
  status: "draft" | "active" | "paused";
  entryPageId: string | null;

  thumbnailUrl?: string | null;
  thumbnailUpdatedAt?: string | null;

  wasPublished?: boolean;
  needsInitialPublish?: boolean;
  hasTemplateUpdate?: boolean;
  hasUnpublishedChanges?: boolean;

  templateId?: number | null;
  template_id?: number | null;
  installedSourceVersionId?: number | null;
  installed_source_version_id?: number | null;
  latestSourceVersionId?: number | null;
  latest_source_version_id?: number | null;
}

interface FunnelContextType {
  funnels: Funnel[];
  isLoading: boolean
  selectedFunnelId: string | null;
  selectedPageId: string | null;
  selectedSectionId: string | null;
  selectedBlockId: string | null;

  setSelectedFunnelId: (id: string | null) => void;
  setSelectedPageId: (id: string | null) => void;
  setSelectedSectionId: (id: string | null) => void;
  setSelectedBlockId: (id: string | null) => void;

  // CRUD de datos
  loadFunnels: (customerId: number) => Promise<void>;
  createFunnel: (customerId: number, name: string, description?: string) => Promise<void>;
  deleteFunnel: (funnelId: string) => Promise<void>;
  updateFunnel: (id: string, updates: Partial<Funnel>) => Promise<void>;

  createPage: (funnelId: string, type: PageType, name?: string) => Promise<void>;
  deletePage: (pageId: string) => Promise<void>;
  updatePage: (pageId: string, updates: Partial<FunnelPage>) => Promise<void>;
  reorderPages: (funnelId: string, pageIds: string[]) => Promise<void>;

  loadPageTree: (pageId: string) => Promise<void>;
  replacePageTree: (funnelId: string, page: FunnelPage) => void;
  updatePageSectionsFromRows: (pageId: string, rows: { id: string; columns: Column[] }[]) => void;

  getPublishedRowsForPage: (pageId?: string | null) => EditorRow[]

  publishFunnel: (funnelId: number) => Promise<{
    ok: boolean;
    versionId: number;
    pages: number;
    routes: { path: string; pageId: number }[];
    label: string;
    thumbnailUrl?: string | null;
  }>;

  getPublishInfo: (funnelId?: string | null) =>
    | { label: string; routes: string[]; ts: number; iso: string }
    | null;
  isDirty: (funnelId?: string | null) => boolean;

  markDirtyCurrent: () => void;

  /** Última vez que se guardó (autosave / manual) */
  lastSavedAt: Date | null;

  /** Hay cambios guardados pero no publicados */
  hasUnpublishedChanges: boolean;
  needsInitialPublish: boolean;

  /** El funnel ya fue publicado al menos una vez */
  wasPublished: boolean;
  publish: (funnelId: string | number) => Promise<{
    ok: boolean;
    versionId: number;
    pages: number;
    routes: { path: string; pageId: number }[];
    label: string;
    thumbnailUrl?: string | null;
  }>;

  addSection: (pageId: string, name?: string) => Promise<void>;
  deleteSection: (sectionId: string) => Promise<void>;
  updateSectionProperties: (sectionId: string, properties: Partial<Section> | Partial<Section["props"]>) => Promise<void>;

  addBlock: (
    sectionId: string,
    blockType: Block["type"],
    columnIndex?: number,
    position?: number
  ) => Promise<string | null>;

  updateBlock: (blockId: string, data: any) => Promise<void>;
  deleteBlock: (blockId: string) => Promise<void>;
  moveBlock: (blockId: string, newPosition: number, targetSectionId?: string) => Promise<void>;
  duplicateBlock: (blockId: string) => Promise<void>;

  moveSection: (sectionId: string, newPosition: number) => Promise<void>;
  updatePageSettings: (funnelId: string, pageId: string, patch: Partial<PageSettings>) => Promise<void>;
  updateFunnelSettings: (
    funnelId: string,
    settings: PageSettings,
    meta?: {
      name?: string
    }
  ) => Promise<void>
}

const FunnelContext = createContext<FunnelContextType | undefined>(undefined);

// helpers
const asStr = (v: any) => String(v);
const headers = { 'content-type': 'application/json' };
const getColumns = (section: Section) => section.columns ?? [];
const toBool = (v: any) =>
  v === true || v === 1 || v === '1' || v === 'true'

export function FunnelProvider({ children }: { children: ReactNode }) {
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [publishedPages, setPublishedPages] = useState<Record<string, any>>({})

  const { userId, loggedIn, isLoading: userLoading } = useCurrentUser();
  const loadedRef = useRef(false);

  const replacePageTree = useCallback((funnelId: string, page: any) => {
    setFunnels(prev =>
      prev.map(f =>
        String(f.id) === String(funnelId)
          ? {
              ...f,
              pages: f.pages.map(p => {
                if (String(p.id) !== String(page.id)) return p;
                const normalizedSections = (page.sections || []).map((s: any) => {
                  const columns = s.columns ?? s.blocks ?? [];
                  return { ...s, blocks: columns, columns };
                });
                return { ...page, sections: normalizedSections };
              }),
            }
          : f
      )
    )
  }, [setFunnels])

  const updatePageSectionsFromRows = useCallback(
    (pageId: string, rows: { id: string; columns: Column[] }[]) => {
      setFunnels(prev =>
        prev.map(f => ({
          ...f,
          pages: f.pages.map(p => {
            if (p.id !== pageId) return p;
            const sections = rows.map((row, index) => {
              const existing = (p.sections || []).find(s => s.id === row.id);
              const columns = row.columns ?? [];
              return {
                id: row.id,
                name: existing?.name ?? `Sección ${index + 1}`,
                props: existing?.props ?? { backgroundColor: 'auto' },
                sortOrder: existing?.sortOrder ?? index,
                blocks: columns,
                columns,
              };
            });
            return { ...p, sections };
          }),
        }))
      );
    },
    []
  );

  const getPublishedRowsForPage = useCallback((pageId?: string | null) => {
    if (!pageId) return []
    const published = publishedPages[String(pageId)]
    const sections = published?.sections ?? []
    return mapPublishedSectionsToRows(sections)
  }, [publishedPages])

  // ====== LOAD & CREATE FUNNEL ======
  const safeParse = (v: any) => {
    try {
      if (v == null) return {}
      if (typeof v === 'object') return v
      return JSON.parse(v)
    } catch {
      return {}
    }
  }

  const loadFunnels = useCallback(async (customerId: number) => {
    try {
      setIsLoading(true)

      const res = await fetch(`/api/funnels?customerId=${customerId}`, {
        cache: 'no-store',
      })

      if (!res.ok) throw new Error('Error loading funnels')

      const data = await res.json()

      // ✅ soporta { ok:true, funnels:[...] } o [...] directo
      const list = Array.isArray(data?.funnels) ? data.funnels : Array.isArray(data) ? data : []

      const normalized: Funnel[] = list.map((f: any) => {
        const entryPageIdRaw = f.entryPageId ?? f.entry_page_id ?? null
        const wasPublished = toBool(f.wasPublished ?? f.was_published)
        const needsInitialPublish = toBool(
          f.needsInitialPublish ?? f.needs_initial_publish
        )
        const hasTemplateUpdate = toBool(
          f.hasTemplateUpdate ?? f.has_template_update
        )

        return {
          id: String(f.id ?? f.funnel_id),
          name: f.name ?? '',
          status: f.status || 'draft',
          settings: safeParse(f.settings ?? f.settings_json),

          thumbnailUrl:
            f.thumbnailUrl ??
            f.thumbnail_url ??
            f.thumbnail ??
            null,

          thumbnailUpdatedAt:
            f.thumbnailUpdatedAt ??
            f.thumbnail_updated_at ??
            null,

          // ✅ Flow
          entryPageId: entryPageIdRaw ? String(entryPageIdRaw) : null,

          wasPublished,
          needsInitialPublish,
          hasTemplateUpdate,
          hasUnpublishedChanges: false,

          templateId: f.templateId ?? f.template_id ?? null,
          template_id: f.template_id ?? f.templateId ?? null,

          installedSourceVersionId:
            f.installedSourceVersionId ?? f.installed_source_version_id ?? null,
          installed_source_version_id:
            f.installed_source_version_id ?? f.installedSourceVersionId ?? null,

          latestSourceVersionId:
            f.latestSourceVersionId ?? f.latest_source_version_id ?? null,
          latest_source_version_id:
            f.latest_source_version_id ?? f.latestSourceVersionId ?? null,

          pages: (f.pages || []).map((p: any) => {
            const nextType = (p.next_type ?? p.nextType ?? p.next?.type ?? 'none') as any
            const nextPageIdRaw = p.next_page_id ?? p.nextPageId ?? p.next?.pageId ?? null
            const nextPageId = nextPageIdRaw ? String(nextPageIdRaw) : null

            return {
              id: String(p.id ?? p.page_id),
              type: p.type ?? p.page_type ?? '',
              name: p.name ?? '',

              settings: safeParse(p.settings ?? p.settings_json),
              sections: Array.isArray(p.sections) ? p.sections : [],

              // ✅ Flow DB fields
              next_type: nextType,
              next_page_id: nextPageId,

              // ✅ Flow UI-friendly
              next: {
                type: nextType,
                pageId: nextType === 'page' ? nextPageId : null,
              },
            }
          }),
        }
      })

      setFunnels(normalized)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!loggedIn || !userId) return
    if (loadedRef.current) return

    loadedRef.current = true
    loadFunnels(userId)
  }, [userId, loggedIn, loadFunnels])

  useEffect(() => {
    if (!funnels.length) return

    setSelectedFunnelId(prev => prev ?? funnels[0].id)
    setSelectedPageId(prev => prev ?? funnels[0]?.pages?.[0]?.id ?? null)
  }, [funnels])

  const createFunnel = async (customerId: number, name: string, description?: string) => {
    const res = await fetch(`/api/funnels`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ customerId, name, description }),
    });
    if (!res.ok) throw new Error('Error creating funnel');
    const created = await res.json(); // {id, name, status}

    // Re-cargar lista y seleccionar el nuevo
    await loadFunnels(customerId);
    setSelectedFunnelId(asStr(created.id));

    // fetch páginas del funnel recién creado para tener sus IDs
    const resF = await fetch(`/api/funnels/${created.id}`);
    if (resF.ok) {
      const full = await resF.json();
      const firstPage = (full.pages || [])[0];
      if (firstPage) {
        setSelectedPageId(asStr(firstPage.id));
        // opcional: trae secciones del primer page si quieres
      }
    }
  };

  // --- NUEVO estado publish & dirty por embudo ---
  const [publishMeta, setPublishMeta] = useState<Record<string, { label: string; routes: string[]; ts: number; iso: string }>>({});
  const [dirtyFunnels, setDirtyFunnels] = useState<Record<string, boolean>>({});
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  // 1) define primero markDirty y clearDirty
  const markDirty = useCallback((funnelId: string) => {
    setDirtyFunnels(prev => ({ ...prev, [funnelId]: true }));
  }, []);

  const clearDirty = useCallback((funnelId: string) => {
    setDirtyFunnels(prev => ({ ...prev, [funnelId]: false }));
  }, []);

  // 2) ahora sí puedes crear markDirtyCurrent usando markDirty
  const markDirtyCurrent = useCallback(() => {
    if (selectedFunnelId) markDirty(selectedFunnelId);
  }, [selectedFunnelId, markDirty]);

  const getPublishInfo = useCallback((funnelId?: string | null) => {
    if (!funnelId) return null;
    return publishMeta[funnelId] || null;
  }, [publishMeta]);

  const isDirty = useCallback((funnelId?: string | null) => {
    if (!funnelId) return false;
    return !!dirtyFunnels[funnelId];
  }, [dirtyFunnels]);

  const selectedFunnel = useMemo(() => {
    if (!selectedFunnelId) return null
    return funnels.find(f => String(f.id) === String(selectedFunnelId)) || null
  }, [funnels, selectedFunnelId])

  const wasPublished = useMemo(() => {
    if (!selectedFunnelId) return false

    return Boolean(
      publishMeta[selectedFunnelId] ||
        selectedFunnel?.wasPublished ||
        selectedFunnel?.was_published
    )
  }, [publishMeta, selectedFunnel, selectedFunnelId])

  const needsInitialPublish = useMemo(() => {
    if (!selectedFunnelId) return false

    if (publishMeta[selectedFunnelId]) return false

    if (selectedFunnel?.needsInitialPublish !== undefined) {
      return Boolean(selectedFunnel.needsInitialPublish)
    }

    if ((selectedFunnel as any)?.needs_initial_publish !== undefined) {
      return Boolean((selectedFunnel as any).needs_initial_publish)
    }

    return !wasPublished
  }, [publishMeta, selectedFunnel, selectedFunnelId, wasPublished])

  const hasUnpublishedChanges = useMemo(() => {
    if (!selectedFunnelId) return false

    return isDirty(selectedFunnelId) || needsInitialPublish
  }, [isDirty, needsInitialPublish, selectedFunnelId])

  function parseLabelToDate(label?: string) {
    // si tu endpoint manda label como "v<timestamp>", lo parseamos
    // ej. v1761627295639 -> 1761627295639
    if (!label) return Date.now();
    const m = /^v(\d{10,16})$/.exec(label);
    if (m) return Number(m[1]);
    return Date.now();
  }

  const publishFunnel = useCallback(async (funnelId: number) => {
    const res = await fetch(`/api/funnels/${funnelId}/publish`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(err || 'Publish failed');
    }
    const data = await res.json();

    const fid = String(funnelId);
    const ts = parseLabelToDate(data?.label);
    const iso = new Date(ts).toISOString();
    const routes = (data?.routes || []).map((r: any) => (r?.path?.startsWith('/') ? r.path : `/${r?.path || ''}`));

    setPublishMeta(prev => ({
      ...prev,
      [fid]: { label: data?.label || 'v-now', routes, ts, iso },
    }))

    setFunnels(prev =>
      prev.map(f =>
        String(f.id) === fid
          ? {
              ...f,
              wasPublished: true,
              needsInitialPublish: false,
              hasUnpublishedChanges: false,
            }
          : f
      )
    )

    if (data?.thumbnailUrl) {
      setFunnels(prev =>
        prev.map(f =>
          String(f.id) === fid
            ? {
                ...f,
                thumbnailUrl: data.thumbnailUrl,
                thumbnailUpdatedAt: new Date().toISOString(),
              }
            : f
        )
      )
    }

    clearDirty(fid)
    return data
    }, [clearDirty])

  // Helpers para ubicar a qué funnel pertenece cada entidad
  const findFunnelIdByPageId = (pageId: string) => {
    const f = funnels.find(ff => ff.pages.some(p => p.id === pageId));
    return f?.id || null;
  };
  const findFunnelIdBySectionId = (sectionId: string) => {
    for (const f of funnels) {
      for (const p of f.pages) {
        if (p.sections?.some(s => s.id === sectionId)) return f.id;
      }
    }
    return null;
  };
  const findFunnelIdByBlockId = (blockId: string) => {
    for (const f of funnels) {
      for (const p of f.pages) {
        for (const s of p.sections) {
          const columns = getColumns(s);
          if (columns.some(c => c.modules?.some(b => b.id === blockId))) return f.id;
        }
      }
    }
    return null;
  };

  // dentro de FunnelProvider
  const loadPageTree = useCallback(async (pageId: string) => {
    const res = await fetch(`/api/funnels/pages/${pageId}`, { cache: 'no-store' });
    if (!res.ok) throw new Error('Error loading page tree');
    const data = await res.json(); // { sections:[{ id, name, sortOrder, props, blocks }], settings,... }

    setFunnels(prev => prev.map(f => ({
      ...f,
      pages: f.pages.map(p => {
        if (p.id !== pageId) return p;
        return {
          ...p,
          settings: data.settings ?? p.settings ?? {},
          settingsDraft: data.settingsDraft ?? p.settingsDraft ?? data.settings ?? p.settings ?? {},
          sections: (data.sections || []).map((s: any) => {
            const columns = s.columns
              ? s.columns.map((c: any) => ({
                  id: String(c.id),
                  span: c.span ?? 12,
                  modules: (c.modules || []).map((b: any) => ({
                    id: String(b.id),
                    type: b.type,
                    data: b.data || {},
                    status: b.status ?? 1,
                  })),
                }))
              : [  
                  {
                    id: `col-${crypto.randomUUID()}`,
                    span: 12,
                    modules: (s.blocks || []).map((b: any) => ({
                      id: String(b.id),
                      type: b.type,
                      data: b.data || {},
                      status: b.status ?? 1,
                    })),
                  },
                ];
            return {
              id: String(s.id),
              name: s.name,
              props: s.props || {},
              blocks: columns,
              columns,
            };
          })
        };
      }),
    })));
    setPublishedPages(prev => ({
      ...prev,
      [String(pageId)]: data, // guarda el árbol published tal cual viene del backend
    }))
  }, []);

  const publish = useCallback(
    async (funnelId: string | number) => {
      const out = await publishFunnel(Number(funnelId))

      // ✅ refresca la página seleccionada para actualizar "public"
      if (selectedPageId) {
        await loadPageTree(String(selectedPageId))
      }

      return out
    },
    [publishFunnel, selectedPageId, loadPageTree]
  )

  useEffect(() => {
    if (!selectedPageId) return
    // si ya lo tenemos cacheado, no refetch
    if (publishedPages[String(selectedPageId)]) return

    loadPageTree(String(selectedPageId)).catch(err => {
      console.error('Error preloading page tree', err)
    })
  }, [selectedPageId, loadPageTree, publishedPages])


  const deleteFunnel = async (funnelId: string) => {
    await fetch(`/api/funnels/${funnelId}`, { method: 'DELETE' });
    setFunnels(prev => prev.filter(f => f.id !== funnelId));
    if (selectedFunnelId === funnelId) {
      setSelectedFunnelId(null);
      setSelectedPageId(null);
      setSelectedSectionId(null);
      setSelectedBlockId(null);
    }
  };

  const updateFunnel = async (id: string, updates: Partial<Funnel>) => {
    await fetch(`/api/funnels/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        name: updates.name,
        status: updates.status,
        slug: (updates as any).slug,
        is_default: (updates as any).is_default,
      }),
    });
    setFunnels(prev => prev.map(f => (f.id === id ? { ...f, ...updates } : f)));
    markDirty(id);
  };

  const createPage = async (funnelId: string, type: PageType, name?: string) => {
  const res = await fetch(`/api/funnels/${funnelId}/pages`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ type, name: name || `Nueva página ${type}` }),
  });
  if (!res.ok) throw new Error('Error creating page');
  const { pageId } = await res.json();

  const createEmptySection = (sectionName = 'Sección') => {
    const columns = [
      {
        id: `col-${crypto.randomUUID()}`,
        span: 12,
        modules: [],
      },
    ];
    return {
      id: `row-${crypto.randomUUID()}`,
      name: sectionName,
      blocks: columns,
      columns,
      props: {
        backgroundColor: 'auto' as SectionBgMode,
      },
    };
  };

  setFunnels(prev => prev.map(f => {
    if (f.id !== funnelId) return f;
    const newPage: FunnelPage = {
      //id: String(pageId),
      id: String(pageId),
      type,
      name: name || `Nueva página ${type}`,
      sections: [createEmptySection()],
      //sections: type === "checkout" ? createCheckoutSections(String(pageId)) : [],  // ✅ Usa createCheckoutSections para checkout
      settings: {},
      settingsDraft: {},
      next_type: 'none' as any,
      next_page_id: null,
      next: {
        type: 'none' as any,
        pageId: null,
      },
    };
    return { ...f, pages: [...f.pages, newPage] };
  }));

  markDirty(funnelId);
  setSelectedPageId(String(pageId));
};

const deletePage = async (pageId: string) => {
  await fetch(`/api/funnels/pages/${pageId}`, { method: 'DELETE' });
  
  setFunnels(prev => prev.map(f => ({
    ...f,
    pages: f.pages.filter(p => p.id !== pageId),
  })));

  const fid = findFunnelIdByPageId(pageId);
  if (fid) markDirty(fid);
  
  if (selectedPageId === pageId) setSelectedPageId(null);
};

const updatePage = async (pageId: string, updates: Partial<FunnelPage>) => {
  setFunnels(prev => prev.map(f => ({
    ...f,
    pages: f.pages.map(p => (p.id === pageId ? { ...p, ...updates } : p)),
  })));

  await fetch(`/api/funnels/pages/${pageId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ name: updates.name, type: updates.type }),
  });

  const fid = findFunnelIdByPageId(pageId);
  if (fid) markDirty(fid);
};

const reorderPages = async (funnelId: string, pageIds: string[]) => {
  setFunnels(prev => prev.map(f => {
    if (f.id !== funnelId) return f;
    const reordered = pageIds.map(id => f.pages.find(p => p.id === id)!).filter(Boolean);
    
    // Persist sort_order
    reordered.forEach(async (page, i) => {
      await fetch(`/api/funnels/pages/${page.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ sort_order: i }),
      });
    });
    
    return { ...f, pages: reordered };
  }));

  markDirty(funnelId);
};

  // ====== PAGE-LEVEL (Settings) ======

  const currentDomain =
  typeof window !== "undefined" ? window.location.hostname : null;

  const savePageTimeout = { current: null as NodeJS.Timeout | null };
  const savePageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const savePageSettingsRemote = async (
    funnelId: string,
    pageId: string,
    settingsDraft: Partial<PageSettings>
  ) => {
    // 1️⃣ Guardar settings (DRAFT)
    const safeSettings =
      settingsDraft && typeof settingsDraft === 'object' ? settingsDraft : {}

    const resPage = await fetch(`/api/funnels/pages/${pageId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings: safeSettings }),
    })

    if (!resPage.ok) {
      toast.error('Error al guardar configuración de la página.')
      return
    }

    // 2) Guardar ruta (si aplica) — usa settingsDraft
    const routePath = (safeSettings as any).routePath
    if (typeof routePath === 'string' && routePath.trim()) {
      const resRoute = await fetch(`/api/funnels/pages/${pageId}/route/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funnel_id: funnelId,
          domain: currentDomain || null,
          path: routePath.trim(),
          is_active: 1,
        }),
      })

      const data = await resRoute.json().catch(() => ({}))

      if (resRoute.status === 409) {
        toast.error(data.error || 'Esa ruta ya está en uso.')
        document.dispatchEvent(
          new CustomEvent('funnel-route-conflict', { detail: routePath })
        )
        return
      }

      if (!resRoute.ok) {
        toast.error('Error al guardar la ruta pública.')
        return
      }
    }

    // 3️⃣ ❌ NO recargues el árbol aquí (causa el “rebote”)
    // await loadPageTree(pageId);

    // 4️⃣ Marcar dirty (esto sí)
    const fid = findFunnelIdByPageId(pageId)
    if (fid) markDirty(fid)
  }

  const updatePageSettings = async (
    funnelId: string,
    pageId: string,
    patch: Partial<PageSettings>
  ) => {
    try {
      // ✅ Optimistic update inmediato (draft)
      // guardamos el mergedDraft en una variable para reusarlo en el debounce
      let mergedDraft: Partial<PageSettings> = {}

      setFunnels(prev =>
        prev.map(f => {
          if (f.id !== funnelId) return f
          return {
            ...f,
            pages: f.pages.map(p => {
              if (p.id !== pageId) return p
              const currentDraft = p.settingsDraft ?? p.settings ?? {}
              mergedDraft = { ...currentDraft, ...patch }
              return { ...p, settingsDraft: mergedDraft }
            }),
          }
        })
      )

      // 🔥 DEBOUNCE REAL (uno solo)
      if (savePageTimeoutRef.current) clearTimeout(savePageTimeoutRef.current)

      savePageTimeoutRef.current = setTimeout(() => {
        // ✅ manda el draft completo mergeado, NO el patch suelto
        savePageSettingsRemote(funnelId, pageId, mergedDraft)
      }, 600)
    } catch (err) {
      console.error('❌ Error en updatePageSettings:', err)
    }
  }

  const saveFunnelTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const saveFunnelSettingsRemote = async (
    funnelId: string,
    settings: PageSettings,
    meta?: {
      name?: string
    }
  ) => {
    const res = await fetch(`/api/funnels/${funnelId}/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        settings,
        name: meta?.name,
      }),
    });

    if (!res.ok) {
      toast.error('Error al guardar configuración del embudo');
      return;
    }

    setLastSavedAt(new Date())
    markDirty(funnelId);
  };

  const updateFunnelSettings = async (
    funnelId: string,
    settings: PageSettings,
    meta?: {
      name?: string
    }
  ) => {
    try {
      // ✅ optimistic update
      setFunnels(prev =>
        prev.map(f =>
          f.id === funnelId
            ? {
                ...f,
                name:
                  typeof meta?.name === 'string'
                    ? meta.name
                    : f.name,
                settings: {
                  ...(f.settings ?? {}),
                  ...(settings ?? {}),
                },
              }
            : f
        )
      )

      // 🔥 debounce (idéntico a page)
      if (saveFunnelTimeoutRef.current) {
        clearTimeout(saveFunnelTimeoutRef.current)
      }

      saveFunnelTimeoutRef.current = setTimeout(() => {
        saveFunnelSettingsRemote(funnelId, settings, meta)
      }, 600)
    } catch (err) {
      console.error('❌ Error en updateFunnelSettings:', err)
    }
  }



  // ====== SECTION CRUD ======
  const addSection = async (pageId: string, name?: string) => {
    const res = await fetch(`/api/funnels/pages/${pageId}/sections`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name: name || 'Nueva sección', props: {} }),
    });
    if (!res.ok) throw new Error('Error creating section');
    const { sectionId } = await res.json();

    setFunnels(prev =>
      prev.map(f => {
        const hasPage = f.pages.some(p => p.id === pageId);
        return {
          ...f,
          pages: f.pages.map(p => {
            if (p.id !== pageId) return p;
            const sec: Section = {
              id: asStr(sectionId),
              name: name || 'Nueva sección',
              sortOrder: (p.sections?.length || 0),
              props: {
                backgroundColor: 'auto',
                backgroundColorValue: '',
                backgroundGradient: '',
                backgroundImage: '',
              },
              columns: [
                {
                  id: `col-${sectionId}-0`,
                  span: 12,
                  modules: [],
                },
              ],
            };

            return { ...p, sections: [...(p.sections || []), sec] };
          }),
        };
      })
    );

    // 🔹 Marca el embudo como “dirty”
    //const f = funnels.find(ff => ff.pages.some(p => p.id === pageId));
    const fid = findFunnelIdByPageId(pageId);
    if (fid) markDirty(fid);

    setSelectedSectionId(asStr(sectionId));
  };

  const deleteSection = async (sectionId: string) => {
    await fetch(`/api/sections/${sectionId}`, { method: 'DELETE' });
    setFunnels(prev => prev.map(f => ({
      ...f,
      pages: f.pages.map(p => ({ ...p, sections: (p.sections || []).filter(s => s.id !== sectionId) })),
    })));
    if (selectedSectionId === sectionId) setSelectedSectionId(null);
  };

  const updateSectionProperties = async (
  sectionId: string,
  properties: Partial<Section> | Partial<Section["props"]>
) => {
  // --- separar meta vs props (solo keys de fondo van a props) ---
  const propKeys = ['backgroundColor', 'backgroundColorValue', 'backgroundGradient', 'backgroundImage'] as const;

  const metaPatch: any = {};
  const propsPatch: any = {};

  // nombre / orden (meta)
  if ((properties as any).name !== undefined) metaPatch.name = (properties as any).name;
  if ((properties as any).sortOrder !== undefined) metaPatch.sort_order = (properties as any).sortOrder;

  // fondo (props)
  for (const k of propKeys) {
    if ((properties as any)[k] !== undefined) {
      propsPatch[k] = (properties as any)[k];
    }
  }

  // --- optimistic update ---
  setFunnels(prev =>
    prev.map(f => ({
      ...f,
      pages: f.pages.map(p => ({
        ...p,
        sections: (p.sections || []).map(s => {
          if (s.id !== sectionId) return s;
          return {
            ...s,
            ...(metaPatch.name !== undefined ? { name: metaPatch.name } : {}),
            // fusionar props existentes con el patch
            props: { ...(s.props || {}), ...propsPatch },
          };
        }),
      })),
    }))
  );

  // --- enviar patch directo (el endpoint ya hace merge con props_json) ---
  const bodyToSend = { ...metaPatch, ...propsPatch };
  const headers = { 'Content-Type': 'application/json' };

  try {
    await fetch(`/api/sections/${sectionId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(bodyToSend),
    });
  } catch (e) {
    console.error('Error updating section properties', e);
    // opcional: revertir estado si quieres manejar rollback
  }

  const fid = findFunnelIdBySectionId(sectionId);
  if (fid) markDirty(fid); // ✅ marcar funnel como "Borrador"
};


  const moveSection = async (sectionId: string, newPosition: number) => {
    // optimistic reordering local
    setFunnels(prev => prev.map(f => ({
      ...f,
      pages: f.pages.map(p => {
        const idx = (p.sections || []).findIndex(s => s.id === sectionId);
        if (idx === -1) return p;
        const arr = [...(p.sections || [])];
        const [moved] = arr.splice(idx, 1);
        arr.splice(Math.max(0, Math.min(newPosition, arr.length)), 0, moved);
        // persistir sort_order en batch (simple: reindex sequential)
        arr.forEach(async (s, i) => {
          await fetch(`/api/sections/${s.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ sort_order: i }),
          });
        });
        return { ...p, sections: arr };
      }),
    })));

    const fid = findFunnelIdBySectionId(sectionId);
    if (fid) markDirty(fid); // ⬅️ NUEVO
  }

  // ====== BLOCK CRUD ======
  const addBlock = async (
    sectionId: string,
    type: Block['type'],
    columnIndex = 0,
    position?: number
  ): Promise<string | null> => {
    try {
      // 1) backend create
      const res = await fetch(`/api/sections/${sectionId}/blocks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, data: {}, columnIndex, position }),
      });
      if (!res.ok) throw new Error('Error creando bloque en draft');

      const { blockId } = await res.json();

      // 2) local state update
      setFunnels(prev =>
        prev.map(f => ({
          ...f,
          pages: f.pages.map(p => ({
            ...p,
            sections: p.sections.map(s => {
              if (s.id !== sectionId) return s;

              const columns = getColumns(s);
              const targetCol = columns[columnIndex] ?? {
                id: `col-${crypto.randomUUID()}`,
                span: 12,
                modules: [],
              };

              const newBlock: Block = { id: String(blockId), type, data: {} };

              const nextColumns = [...columns];
              if (!nextColumns[columnIndex]) nextColumns[columnIndex] = targetCol;

              const insertAt =
                typeof position === 'number'
                  ? Math.max(0, Math.min(position, nextColumns[columnIndex].modules.length))
                  : nextColumns[columnIndex].modules.length;

              const nextModules = [...nextColumns[columnIndex].modules];
              nextModules.splice(insertAt, 0, newBlock);

              nextColumns[columnIndex] = { ...nextColumns[columnIndex], modules: nextModules };

              return { ...s, columns: nextColumns, blocks: nextColumns as any }; // si aún tienes compat
            }),
          })),
        }))
      );

      const funnelId = findFunnelIdBySectionId(sectionId);
      if (funnelId) markDirty(funnelId);

      return String(blockId);
    } catch (e) {
      console.error(e);
      return null;
    }
  };


  const updateBlock = async (blockId: string, data: any) => {
    // optimistic
    setFunnels(prev => prev.map(f => ({
      ...f,
      pages: f.pages.map(p => ({
        ...p,
        sections: (p.sections || []).map(s => {
          const columns = getColumns(s).map(col => ({
            ...col,
            modules: col.modules.map(b => (b.id === blockId ? { ...b, data } : b)),
          }));
          return { ...s, blocks: columns, columns };
        }),
      })),
    })));

    await fetch(`/api/blocks/${blockId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ data }),
    });

    const fid = findFunnelIdByBlockId(blockId);
    if (fid) markDirty(fid); // ⬅️ NUEVO
  };

  const deleteBlock = async (blockId: string) => {
    await fetch(`/api/blocks/${blockId}`, { method: 'DELETE' });
    setFunnels(prev => prev.map(f => ({
      ...f,
      pages: f.pages.map(p => ({
        ...p,
        sections: (p.sections || []).map(s => {
          const columns = getColumns(s).map(col => ({
            ...col,
            modules: col.modules.filter(b => b.id !== blockId),
          }));
          return { ...s, blocks: columns, columns };
        }),
      })),
    })));

    const fid = findFunnelIdByBlockId(blockId);
    if (fid) markDirty(fid); // ⬅️ NUEVO

    if (selectedBlockId === blockId) setSelectedBlockId(null);
  };

  const moveBlock = async (blockId: string, newPosition: number, targetSectionId?: string) => {
    setFunnels(prev => prev.map(f => ({
      ...f,
      pages: f.pages.map(p => {
        // localizar sección origen y bloque
        let moved: Block | null = null;
        const sectionsUpdated = (p.sections || []).map(s => {
          const columns = getColumns(s);
          let found = false;
          const updatedColumns = columns.map(col => {
            const idx = col.modules.findIndex(b => b.id === blockId);
            if (idx === -1) return col;
            const nextModules = [...col.modules];
            [moved] = nextModules.splice(idx, 1);
            found = true;
            return { ...col, modules: nextModules };
          });
          if (!found) return s;
          return { ...s, blocks: updatedColumns, columns: updatedColumns };
        });

        if (!moved) return p;

        // insertar en destino
        const destId = targetSectionId || sectionsUpdated.find(s => getColumns(s).some(c => c.modules.some(b => b.id === blockId)))?.id;
        const finalSections = sectionsUpdated.map(s => {
          if (s.id !== destId) return s;
          const columns = getColumns(s);
          const firstColumn = columns[0] ?? {
            id: `col-${crypto.randomUUID()}`,
            span: 12,
            modules: [],
          };
          const nextColumns = columns.length ? [...columns] : [firstColumn];
          const targetColumn = nextColumns[0];
          const insert = Math.max(0, Math.min(newPosition, targetColumn.modules.length));
          const nextModules = [...targetColumn.modules];
          nextModules.splice(insert, 0, moved!);
          nextColumns[0] = { ...targetColumn, modules: nextModules };
          return { ...s, blocks: nextColumns, columns: nextColumns };
        });

        return { ...p, sections: finalSections };
      }),
    })));

    const fid = targetSectionId ? findFunnelIdBySectionId(targetSectionId) : findFunnelIdByBlockId(blockId);
    if (fid) markDirty(fid); // ⬅️ NUEVO

  };

  const duplicateBlock = async (blockId: string) => {
  let src:
    | { sectionId: string; data: any; type: Block['type']; columnIndex: number; insertAt: number }
    | null = null;

  for (const f of funnels) {
    for (const p of f.pages) {
      for (const s of p.sections || []) {
        const columns = getColumns(s);
        for (let cIndex = 0; cIndex < columns.length; cIndex++) {
          const col = columns[cIndex];
          const idx = col.modules.findIndex(m => String(m.id) === String(blockId));
          if (idx !== -1) {
            const b = col.modules[idx];
            src = {
              sectionId: String(s.id),
              data: b.data,
              type: b.type as Block['type'],
              columnIndex: cIndex,
              insertAt: idx + 1, // justo después del original
            };
            break;
          }
        }
        if (src) break;
      }
      if (src) break;
    }
    if (src) break;
  }

  if (!src) return;

  const fid = findFunnelIdByBlockId(blockId);
  if (fid) markDirty(fid);

  // crea el nuevo bloque en esa misma columna y posición
  const newBlockId = await addBlock(src.sectionId, src.type, src.columnIndex, src.insertAt);

  // clonar data real al nuevo bloque
  if (newBlockId) {
    await updateBlock(newBlockId, src.data);
  }
};


  const value = useMemo<FunnelContextType>(() => ({
    funnels,
    isLoading,
    selectedFunnelId,
    selectedPageId,
    selectedSectionId,
    selectedBlockId,

    setSelectedFunnelId,
    setSelectedPageId,
    setSelectedSectionId,
    setSelectedBlockId,
    getPublishedRowsForPage,

    loadFunnels,
    createFunnel,
    deleteFunnel,
    updateFunnel,
    updateFunnelSettings,
    loadPageTree,
    replacePageTree,
    updatePageSectionsFromRows,
    publishFunnel,
    getPublishInfo,
    isDirty,   
    publish,
    wasPublished,
    hasUnpublishedChanges,
    needsInitialPublish,
    lastSavedAt,
    markDirtyCurrent,

    addSection,
    deleteSection,
    updateSectionProperties,

    addBlock,
    updateBlock,
    deleteBlock,
    moveBlock,
    duplicateBlock,

    moveSection,
    updatePageSettings,
    createPage,
    deletePage,
    updatePage,
    reorderPages,
  }), [
    funnels,
    isLoading,
    selectedFunnelId,
    selectedPageId,
    selectedSectionId,
    selectedBlockId,
    loadFunnels,
    createFunnel,
    deleteFunnel,
    updateFunnel,
    updateFunnelSettings,
    loadPageTree,
    replacePageTree,
    updatePageSectionsFromRows,
    publishFunnel,
    getPublishInfo,
    isDirty,
    publish,
    wasPublished,
    hasUnpublishedChanges,
    needsInitialPublish,
    lastSavedAt,
    markDirtyCurrent,
    addSection,
    deleteSection,
    updateSectionProperties,
    addBlock,
    updateBlock,
    deleteBlock,
    moveBlock,
    duplicateBlock,
    moveSection,
    updatePageSettings,
    createPage,
    deletePage,
    updatePage,
    reorderPages,
  ]);

  return (
    <FunnelContext.Provider value={value}>
      {children}
    </FunnelContext.Provider>
  );
}

export function useFunnel() {
  const context = useContext(FunnelContext);
  if (context === undefined) {
    throw new Error("useFunnel must be used within a FunnelProvider");
  }
  return context;
}
